import React from 'react';
import {noop} from 'lodash';
import {useDispatch, useSelector} from 'react-redux';
import {Formik} from 'formik';
import {Store} from 'src/types/store';
import {saveAboutMe} from 'shared/about-me/actions/about-me';
import AcademicInterests from 'shared/components/academic-interests';
import Label from 'shared/components/label';
import TextArea from 'src/components/text-area';
import YearDropdown from 'src/components/year-dropdown';
import InstitutionTypeaheadWithModal from 'shared/institution-typeahead/components/institution-typeahead-with-modal';
import FormCard from 'shared/portfolio/components/form-card';
import PortfolioFormFooter from 'src/components/PortfolioFormFooter';
import {classOfYears} from 'shared/portfolio/utils';

interface Props {
  name: string;
  setEditingFalse: () => void;
}

const AboutMeForm = ({name, setEditingFalse}: Props) => {
  const dispatch = useDispatch();
  const student = useSelector((state: Store) => state.student);

  return (
    <FormCard id="high-school-about-me-form">
      <div className="about-me-form">
        <Formik
          initialValues={{
            _id: student._id,
            highSchoolInfo: {graduationYear: student.highSchoolInfo.graduationYear},
            bio: student.bio || '',
            currentInstitution: {
              country: student.currentInstitution.country,
              city: student.currentInstitution.city,
              subdivision: student.currentInstitution.subdivision,
              parentId: student.currentInstitution.parentId,
              name: student.currentInstitution.name,
              type: student.currentInstitution.type,
              ineligibleCountry: false,
            },
            academicInterestIds: student.academicInterestIds,
          }}
          validate={(values) => {
            const errors: {
              highSchoolInfo?: {graduationYear?: string};
              bio?: string;
              currentInstitution?: {name?: string};
            } = {};

            if (!values.highSchoolInfo.graduationYear) {
              errors.highSchoolInfo = {
                graduationYear: 'Graduation year is required.',
              };
            }

            if (values.bio.length >= 140) {
              errors.bio = 'Bio must be less than 140 characters.';
            }

            if (!values.currentInstitution.name) {
              errors.currentInstitution = {
                name: 'High school is required.',
              };
            }

            return errors;
          }}
          onSubmit={(values) => {
            dispatch(saveAboutMe(values)).then(() => setEditingFalse());
          }}
        >
          {({handleSubmit, values, errors, setFieldValue, getFieldProps}) => (
            <form onSubmit={handleSubmit} autoComplete="off">
              <h1 className="about-me-form-header">{name}</h1>
              <div className="about-me-form-class-year">
                <YearDropdown
                  label="Class Year"
                  value={values.highSchoolInfo.graduationYear}
                  error={errors.highSchoolInfo?.graduationYear}
                  onChange={(e) => {
                    setFieldValue('highSchoolInfo.graduationYear', e.target.value);
                  }}
                  years={classOfYears(values.highSchoolInfo.graduationYear)}
                />
              </div>
              <InstitutionTypeaheadWithModal institutionType="high-school" />
              <Label>
                Bio
                <TextArea
                  {...getFieldProps('bio')}
                  error={errors.bio}
                  placeholder="Enter a brief description about yourself."
                  maxLength={140}
                  showMaxLength
                />
              </Label>
              <AcademicInterests label="What are you interested in studying? (Add up to 3)" />
              <div className="about-me-form-side-by-side-grid">
                <p className="about-me-form-interest-subtext">
                  Colleges will be able to see your interests.
                </p>
                <PortfolioFormFooter
                  hideDelete
                  _id="about-me-footer"
                  onDelete={noop}
                  onCancel={setEditingFalse}
                  formName="about-me about-me-form"
                />
              </div>
            </form>
          )}
        </Formik>
      </div>
    </FormCard>
  );
};

export default AboutMeForm;
